import React from 'react';
import { useSelectedCompanyHotel } from '../../../provider/useSelectedCompanyHotel';
import Competitor from '@app/model/company_hotel/Competitor';
import InlineTagSelector from '@app/site/secured/rateshopper/rates/settings/page/competitor-tags/InlineTagSelector';
import CompanyHotel from '@app/model/company_hotel/CompanyHotel';
import CompetitorTag from '@app/model/company_hotel/CompetitorTag';
import {
	useAddCompanyHotelCompetitorTagToCompetitorMutation,
	useCreateCompanyHotelCompetitorTagMutation,
	useDeleteCompanyHotelCompetitorTagMutation,
	useEditCompanyHotelCompetitorTagMutation,
	useRemoveCompanyHotelCompetitorTagFromCompetitorMutation,
} from '@app/model/company_hotel/service/CompanyHotelServiceHooks';
import useToaster from '@app/component/overlays/toaster/ToasterHook';
import { useTranslation } from '@app/component/translation/Translation';


const RateshopperCompetitorTagSettingsPage = React.memo(() => {

	const { companyHotel } = useSelectedCompanyHotel();

	return <main>

		<div className="bg-white p-4 sm:p-10 rounded-xl">
			<div className="mr-0 sm:mr-4 mb-4 lg:mb-0">
				<div className="pb-3 flex flex-wrap items-center justify-between border-b border-gray-200">
					<div>
						<h2 className="text-base font-semibold leading-5 text-gray-900">Manage groups of competitors</h2>
						<p className="text-sm leading-6 text-gray-500">
							Create groups by assigning different tags to your competitors for smarter filtering at the dashboard.
						</p>
					</div>
				</div>
			</div>
			<div className="mt-8">
				<div className="rounded-lg bg-gray-50 ring-1 ring-gray-800/10 shadow">
					<div className="px-4 py-5 sm:p-6 space-y-2">
						{companyHotel.competitors.map((competitor, index) => <CompetitorTagsRow companyHotel={companyHotel}
																				competitor={competitor}
																				key={`competitor_${index}`} />)}
					</div>
				</div>
			</div>
		</div>
	</main>;
});

const CompetitorTagsRow: React.FunctionComponent<{
	companyHotel: CompanyHotel,
	competitor: Competitor
}> = ({ companyHotel, competitor }) => {
	const toaster = useToaster();
	const { t } = useTranslation();

	const createCompanyHotelCompetitorTagMutation = useCreateCompanyHotelCompetitorTagMutation({
		onSuccess: () => {
			toaster.success('Tag', 'Tag created successfully!');
		},
		onError: (error: string) => toaster.error('Tag', t(error)),
	});
	const editCompanyHotelCompetitorTagMutation = useEditCompanyHotelCompetitorTagMutation({
		onSuccess: () => {
			toaster.success('Tag', 'Tag saved successfully!');
		},
		onError: (error: string) => toaster.error('Tag', t(error)),
	});
	const deleteCompanyHotelCompetitorTagMutation = useDeleteCompanyHotelCompetitorTagMutation({
		onSuccess: () => {
			toaster.success('Tag', 'Tag deleted successfully!');
		},
		onError: (error: string) => toaster.error('Tag', t(error)),
	});

	const addCompanyHotelCompetitorTagToCompetitorMutation = useAddCompanyHotelCompetitorTagToCompetitorMutation();
	const removeCompanyHotelCompetitorTagFromCompetitorMutation = useRemoveCompanyHotelCompetitorTagFromCompetitorMutation();

	const onCreate = async (name: string, color: string) => {
		await createCompanyHotelCompetitorTagMutation.mutateAsync({
			companyHotelId: companyHotel.id,
			name,
			color,
		});
		return await Promise.resolve();
	};

	const onEdit = async (id: number, name: string, color: string) => {
		await editCompanyHotelCompetitorTagMutation.mutateAsync({
			companyHotelId: companyHotel.id,
			competitorTagId: id,
			name,
			color,
		});
		return await Promise.resolve();
	};

	const onDelete = async (id: number) => {
		await deleteCompanyHotelCompetitorTagMutation.mutateAsync({
			companyHotelId: companyHotel.id,
			competitorTagId: id,
		});
		return await Promise.resolve();
	};

	const onSelect = async (tag: CompetitorTag) => {
		await addCompanyHotelCompetitorTagToCompetitorMutation.mutateAsync({
			companyHotelId: companyHotel.id,
			competitorId: competitor.id,
			competitorTagId: tag.id!,
		});
		return await Promise.resolve();
	};

	const onDeselect = async (tag: CompetitorTag) => {
		await removeCompanyHotelCompetitorTagFromCompetitorMutation.mutateAsync({
			companyHotelId: companyHotel.id,
			competitorId: competitor.id,
			competitorTagId: tag.id!,
		});
		return await Promise.resolve();
	};


	return <div
		className="flex items-center justify-start gap-x-2 rounded-md bg-white px-3 py-2 ring-1 ring-black ring-opacity-5 text-sm">
		<span className="font-medium text-gray-700 mr-4">{competitor.name}</span>
		<InlineTagSelector availableTags={companyHotel.getCompetitorTags()}
											 selectedTags={competitor.tags || []}
											 onCreate={onCreate}
											 onEdit={onEdit}
											 onSelect={onSelect}
											 onDeselect={onDeselect}
											 onDelete={onDelete} />
	</div>;
};

export default RateshopperCompetitorTagSettingsPage;